import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Loader2, FileText, Upload, X, BookOpen, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LessonSlidesViewer from "./LessonSlidesViewer";

interface Props {
  lessonId: string;
  lessonTitle: string;
  lessonContent: string | null;
  nurseryId: string;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function GenerateExplanationButton({ lessonId, lessonTitle, lessonContent, nurseryId }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [generatingImages, setGeneratingImages] = useState<Set<number>>(new Set());
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [sourceTab, setSourceTab] = useState<string>(lessonContent ? "text" : "file");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: explanation, refetch } = useQuery({
    queryKey: ["lesson-explanation", lessonId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_explanations")
        .select("*")
        .eq("lesson_id", lessonId)
        .maybeSingle();
      return data;
    },
  });

  const hasExplanation = explanation?.status === "ready" && (explanation.slides as any[])?.length > 0;

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      let fileBase64: string | undefined;
      let fileMimeType: string | undefined;

      if (sourceTab === "file" && uploadedFile) {
        fileBase64 = await fileToBase64(uploadedFile);
        fileMimeType = uploadedFile.type;
      }

      const { data, error } = await supabase.functions.invoke("generate-lesson-explanation", {
        body: {
          lessonId,
          lessonTitle,
          lessonContent: sourceTab === "text" ? lessonContent : null,
          fileBase64,
          fileMimeType,
          nurseryId,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "تم إنشاء الشرح بنجاح! 🎉" });
      setDialogOpen(false);
      await refetch();
      setViewerOpen(true);
    } catch (err: any) {
      console.error("Generate explanation error:", err);
      toast({
        title: "خطأ",
        description: err.message || "فشل في توليد الشرح",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  }, [lessonId, lessonTitle, lessonContent, nurseryId, sourceTab, uploadedFile, toast, refetch]);

  const handleGenerateImage = useCallback(async (slideIndex: number, imagePrompt: string) => {
    setGeneratingImages((prev) => new Set([...prev, slideIndex]));
    try {
      const { data, error } = await supabase.functions.invoke("generate-slide-image", {
        body: {
          explanationId: explanation?.id,
          slideIndex,
          imagePrompt,
          nurseryId,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await refetch();
      toast({ title: "تم توليد الصورة بنجاح! 🎨" });
    } catch (err: any) {
      console.error("Image gen error:", err);
      toast({
        title: "خطأ في توليد الصورة",
        description: err.message || "حاول مرة أخرى",
        variant: "destructive",
      });
    } finally {
      setGeneratingImages((prev) => {
        const next = new Set(prev);
        next.delete(slideIndex);
        return next;
      });
    }
  }, [explanation?.id, nurseryId, refetch, toast]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الملف أكبر من 10MB", variant: "destructive" });
      return;
    }
    setUploadedFile(file);
  };

  return (
    <>
      {/* Main Buttons */}
      <div className="flex gap-2">
        {hasExplanation && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewerOpen(true)}
            className="gap-1.5"
          >
            <Eye className="w-4 h-4" />
            عرض الشرح
          </Button>
        )}
        <Button
          variant={hasExplanation ? "ghost" : "default"}
          size="sm"
          onClick={() => setDialogOpen(true)}
          className="gap-1.5"
        >
          <Sparkles className="w-4 h-4" />
          {hasExplanation ? "إعادة توليد" : "📚 إنشاء شرح تلقائي"}
        </Button>
      </div>

      {/* Generate Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              إنشاء شرح تلقائي بالذكاء الاصطناعي
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              سيقوم الذكاء الاصطناعي بتحليل محتوى الدرس وتحويله إلى شرائح ملونة مبسطة وممتعة للأطفال مع صور توضيحية وأسئلة تفاعلية.
            </p>

            <Tabs value={sourceTab} onValueChange={setSourceTab}>
              <TabsList className="w-full">
                <TabsTrigger value="text" className="flex-1 gap-1" disabled={!lessonContent}>
                  <FileText className="w-3.5 h-3.5" />
                  نص الدرس
                </TabsTrigger>
                <TabsTrigger value="file" className="flex-1 gap-1">
                  <Upload className="w-3.5 h-3.5" />
                  رفع ملف PDF/صورة
                </TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="mt-3">
                {lessonContent ? (
                  <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{lessonContent}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    لا يوجد محتوى نصي. يرجى رفع ملف PDF أو صورة.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="file" className="mt-3">
                {uploadedFile ? (
                  <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-3">
                    <FileText className="w-5 h-5 text-primary shrink-0" />
                    <span className="text-sm truncate flex-1">{uploadedFile.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setUploadedFile(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors block">
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">اضغط لرفع ملف PDF أو صورة</p>
                    <p className="text-xs text-muted-foreground mt-1">حتى 10MB</p>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </label>
                )}
              </TabsContent>
            </Tabs>

            <Button
              onClick={handleGenerate}
              disabled={generating || (sourceTab === "text" && !lessonContent) || (sourceTab === "file" && !uploadedFile)}
              className="w-full gap-2"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  جاري إنشاء الشرح...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  إنشاء الشرح التلقائي ✨
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Slides Viewer */}
      {viewerOpen && hasExplanation && (
        <LessonSlidesViewer
          slides={explanation.slides as any[]}
          lessonTitle={lessonTitle}
          onClose={() => setViewerOpen(false)}
          onGenerateImage={handleGenerateImage}
          generatingImages={generatingImages}
        />
      )}
    </>
  );
}
